import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { appRoute } from 'src/app/app-route.constants';
import { StorageService } from 'src/app/common/services/storage.service';
import { addZpaxMessageListener, sendZpaxMessage } from 'src/app/common/utils/myzpax-messaging';
import { AuthService } from 'src/app/services/auth.service';
import { MyZpaxService } from 'src/app/services/myzpax.service';
import { isExcludedStateRoute, isReactAppRoute } from 'src/app/common/utils/myzpax-routes.util';
import { homeUrl, navigateHome } from 'src/app/common/utils/home-route.util';

const LOGIN_STATUS_MAX_ATTEMPTS = 3;
const LOGIN_STATUS_RETRY_INTERVAL_MS = 1000;

@Component({
  selector: 'app-init',
  standalone: true,
  imports: [NgxSpinnerModule],
  template: `<ngx-spinner bdColor="rgba(255,255,255,0.7)" size="medium" color="#4663AE" type="square-jelly-box" [fullScreen]="true" [disableAnimation]="false"></ngx-spinner>`,
})
export class InitComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _myzpaxService = inject(MyZpaxService);
  private _storageService = inject(StorageService);
  private _authService = inject(AuthService);
  private _spinner = inject(NgxSpinnerService);

  private unsubscribe?: () => void;
  private retryTimer?: ReturnType<typeof setInterval>;
  private attempts = 0;
  private resolved = false;

  ngOnInit(): void {
    if (!this._myzpaxService.isInMyzpax) {
      navigateHome(this._router, true);
      return;
    }

    this._spinner.show();

    const state = this._route.snapshot.queryParamMap.get('state') ?? undefined;

    this.unsubscribe = addZpaxMessageListener('login_status', (message) => {
      this.handleLoginStatus(message.data, state);
    });

    this.requestLoginStatus();
  }

  ngOnDestroy(): void {
    this.stopRetries();
    this.unsubscribe?.();
  }

  private requestLoginStatus(): void {
    this.attempts = 1;
    sendZpaxMessage('login_status');

    this.retryTimer = setInterval(() => {
      if (this.attempts >= LOGIN_STATUS_MAX_ATTEMPTS) {
        this.stopRetries();
        this.handleUnconfirmedLogin();
        return;
      }
      this.attempts++;
      sendZpaxMessage('login_status');
    }, LOGIN_STATUS_RETRY_INTERVAL_MS);
  }

  private stopRetries(): void {
    clearInterval(this.retryTimer);
  }

  /**
   * myzPAX could not confirm a logged-in user (explicit `null`, or no response
   * at all). `AuthService.reAuthenticate` clears the session before landing here
   * but deliberately preserves `lastActiveUserId` (see `sessionStateKeys`), so
   * its presence means a RehabConnect session was active moments ago — this is
   * a mid-use session expiry, not "no access", so ask myzPAX to prompt login
   * rather than showing the promo page to someone who was just using the app.
   * Otherwise there was never a session here — show the promo page instead of
   * leaving the tile blank.
   */
  private handleUnconfirmedLogin(): void {
    this.resolved = true;
    this._spinner.hide();
    if (this._storageService.getLastActiveUserId() !== null) {
      sendZpaxMessage('open_login_popup');
      return;
    }
    this._router.navigate([`/${appRoute.marketing}`], { replaceUrl: true });
  }

  private handleLoginStatus(myzpaxUserId: number | null, state: string | undefined): void {
    if (this.resolved) {
      return;
    }
    this.stopRetries();

    if (myzpaxUserId === null) {
      this.handleUnconfirmedLogin();
      return;
    }

    const sessionActive = this._storageService.getUser() !== null;
    const storedSsoUserId = this._storageService.getSsoUserId();
    const idsMatch = storedSsoUserId !== null && String(myzpaxUserId) === storedSsoUserId;

    this.resolved = true;
    this._spinner.hide();

    if (sessionActive && idsMatch) {
      const user = this.getStoredUser();
      if (user?.npi) {
        // NPI/physician users cannot use Rehab Connect inside myzPAX. Break out
        // of the iframe to standalone Rehab, otherwise navigating to an app route
        // would loop: /home -> userValidatedGuard (npi) -> /init -> here again.
        this.redirectOutsideMyzpax();
        return;
      }

      const path = this.getSafePath(state);
      if (isReactAppRoute(path)) {
        window.location.href = path;
      } else {
        this._router.navigateByUrl(path, { replaceUrl: true });
      }
    } else {
      this._storageService.clearUserSession();
      this._authService.redirectToSSOLoginPage(state);
    }
  }

  private getStoredUser(): { npi?: unknown } | null {
    const user = this._storageService.getUser();
    return user ? JSON.parse(user) : null;
  }

  private redirectOutsideMyzpax(): void {
    // Deliberately the Angular route even when React owns Home: this is a
    // trampoline, not a destination. `userValidatedGuard` rejects NPI users and
    // runs the SSO redirect from the top-level window. React has no equivalent
    // guard, so /rc/home would render the dashboard to a physician instead.
    const target = `${window.location.origin}/${appRoute.home}`;
    if (window.top) {
      window.top.location.href = target;
    } else {
      window.location.href = target;
    }
  }

  private getSafePath(state: string | undefined): string {
    if (!state || isExcludedStateRoute(state)) {
      return homeUrl();
    }
    return state.startsWith('/') ? state : `/${state}`;
  }
}
